import { SwapRightOutlined } from '@ant-design/icons';
import { Button, DatePicker, Divider, InputNumber, Row, Space } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DATE_PICKER_FORMAT, PREFIX } from '@app/constants';
import { useMemoizeObject, useWindowSize } from '@app/hooks';
import {
  CHF_DATE_END,
  CHF_DATE_START,
  CHF_PERIOD,
  CHF_VISITS_FILTER,
} from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { ClientFiltersContext, NotificationContext } from '@app/providers';
import { correctRangeValues } from '@app/utils';

import {
  handleDateFilters,
  hideFilterBlock,
} from '@pages/Clients/ClientsList/utils';

import { dateFormatList } from '@pages/Clients/ClientsList/constants/clientFilterDates';
import {
  LAST_MONTH,
  LAST_N_DAYS,
  LAST_THREE_MONTHS,
  LAST_WEEK,
  TODAY,
  YESTERDAY,
} from '@pages/Clients/ClientsList/constants/periods';

import type { NClientsList } from '@pages/Clients/ClientsList/ClientsList.types';
import { initialFiltersConfig } from '@pages/Clients/ClientsList/data/initialFiltersConfig';

import '@pages/Clients/ClientsList/styles/DatesRange.desktop.scss';

export const VisitsPeriod = ({
  filterGroupIndex,
}: {
  filterGroupIndex: number;
}) => {
  const { filters, handleDisabling, setFilters, setUpServerDataChunk } =
    useContext(ClientFiltersContext);
  const { showNotification } = useContext(NotificationContext);

  const { innerWidth } = useWindowSize();

  const today = new Date();

  const [fastFilter, setFastFilter] = useState<NClientsList.Period | null>(
    null
  );
  const [lastNDays, setLastNDays] = useState<number | null>(null);
  const [isDatesSegmentMode, setIsDatesSegmentMode] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { DateStart = null, DateEnd = null } =
    filters?.VisitsFilter?.[filterGroupIndex]?.Values?.[0];

  const memoizedFilters = useMemoizeObject(filters) || initialFiltersConfig;

  const startDate: string | undefined = useMemo(() => {
    if (DateStart === undefined) return undefined;

    return DateStart ? dayjs(DateStart).format() : undefined;
  }, [DateStart]);

  const endDate: string | undefined = useMemo(() => {
    if (DateEnd === undefined) return undefined;

    return DateEnd ? dayjs(DateEnd).format() : undefined;
  }, [DateEnd]);

  const updateDateFilters = useCallback(
    (startDate: string | null, endDate: string | null) => {
      handleDateFilters({
        startDate,
        endDate,
        targetFiltersAreaId: CHF_VISITS_FILTER,
        filterGroupIndex,
        filters,
        setFilters,
      });
    },
    [filterGroupIndex, filters, setFilters]
  );

  useEffect(() => {
    correctRangeValues({
      inputStart: startDate,
      inputEnd: endDate,
      cbToSaveBothValues: ([localStartDate, localEndDate]) =>
        updateDateFilters(
          localStartDate
            ? dayjs(localStartDate).format(dateFormatList[1])
            : null,
          localEndDate ? dayjs(localEndDate).format(dateFormatList[1]) : null
        ),
      showNotification,
    });
  }, [
    filterGroupIndex,
    startDate,
    endDate,
    memoizedFilters,
    showNotification,
    setFilters,
    updateDateFilters,
  ]);

  useEffect(() => {
    const isFreeRangeSegment = fastFilter !== LAST_N_DAYS;

    if ((!startDate || !endDate) && isFreeRangeSegment) setFastFilter(null);
  }, [startDate, endDate, fastFilter]);

  useEffect(() => {
    const isFastFilter = fastFilter === LAST_N_DAYS;

    !isFastFilter && setLastNDays(null);
  }, [fastFilter]);

  const clearDateInput = useCallback(
    (key: NClientsList.DateKey): void => {
      const handledKey =
        filters?.[CHF_VISITS_FILTER]?.[filterGroupIndex]?.Values?.[0]?.[key];

      if (handledKey) {
        const [{ DateStart, DateEnd }] =
          filters?.[CHF_VISITS_FILTER]?.[filterGroupIndex]?.Values;

        updateDateFilters(
          key === CHF_DATE_START ? null : DateStart,
          key === CHF_DATE_END ? null : DateEnd
        );
      }

      setIsDatesSegmentMode(false);
    },
    [filterGroupIndex, filters, updateDateFilters]
  );

  const handleClickByFreeValueSegment = useCallback(() => {
    updateDateFilters(null, null);

    setIsDatesSegmentMode(true);
    setFastFilter(LAST_N_DAYS);

    inputRef.current?.focus();
  }, [updateDateFilters]);

  return (
    <div
      className={`${PREFIX}chf-dates-range`}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection:
            940 < innerWidth && innerWidth <= 1105 ? 'column' : 'row',
          rowGap: 940 < innerWidth && innerWidth <= 1105 ? '16px' : 'auto',
        }}
      >
        <Space className="filter-content" direction="vertical">
          <Space size="small">
            <DatePicker
              className="date-input"
              placeholder={'от'}
              size="small"
              showNow={false}
              format={DATE_PICKER_FORMAT}
              value={startDate === undefined ? null : dayjs(startDate)}
              onChange={(value) => {
                setIsDatesSegmentMode(false);

                if (value === null) {
                  clearDateInput(CHF_DATE_START);
                } else {
                  updateDateFilters(
                    value.format(dateFormatList[1]),
                    endDate ? dayjs(endDate).format(dateFormatList[1]) : null
                  );

                  lastNDays && setLastNDays(null);
                }
              }}
            />

            <SwapRightOutlined />

            <DatePicker
              className="date-input"
              placeholder={'до'}
              size="small"
              showNow={false}
              format={DATE_PICKER_FORMAT}
              value={endDate === undefined ? null : dayjs(endDate)}
              onChange={(value) => {
                setIsDatesSegmentMode(false);

                if (value === null) {
                  clearDateInput(CHF_DATE_END);
                } else {
                  updateDateFilters(
                    startDate
                      ? dayjs(startDate).format(dateFormatList[1])
                      : null,
                    value.format(dateFormatList[1])
                  );

                  lastNDays && setLastNDays(null);
                }
              }}
            />
          </Space>

          <Divider className="date-inputs-divider" />

          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === TODAY ? 'primary' : 'text'
            }
            block
            onClick={() => {
              updateDateFilters(
                moment(today).format(dateFormatList[1]),
                moment(today).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(TODAY);
            }}
          >
            Сегодня
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === YESTERDAY
                ? 'primary'
                : 'text'
            }
            block
            onClick={() => {
              const yesterday: Date = new Date(today);

              yesterday.setDate(yesterday.getDate() - 1);
              updateDateFilters(
                moment(yesterday).format(dateFormatList[1]),
                moment(yesterday).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(YESTERDAY);
            }}
          >
            Вчера
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === LAST_WEEK
                ? 'primary'
                : 'text'
            }
            block
            onClick={() => {
              const week = new Date(today);
              week.setDate(week.getDate() - 7);

              updateDateFilters(
                moment(week).format(dateFormatList[1]),
                moment(today).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(LAST_WEEK);
            }}
          >
            Последняя неделя
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === LAST_MONTH
                ? 'primary'
                : 'text'
            }
            block
            onClick={() => {
              const month = new Date(today);

              month.setDate(month.getDate() - 30);
              updateDateFilters(
                moment(month).format(dateFormatList[1]),
                moment(today).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(LAST_MONTH);
            }}
          >
            Последний месяц
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === LAST_THREE_MONTHS
                ? 'primary'
                : 'text'
            }
            block
            onClick={() => {
              const threeMonths = new Date(today);

              threeMonths.setDate(threeMonths.getDate() - 90);
              updateDateFilters(
                moment(threeMonths).format(dateFormatList[1]),
                moment(today).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(LAST_THREE_MONTHS);
            }}
          >
            Последние 3 месяца
          </Button>
          <Space onClick={handleClickByFreeValueSegment}>
            <Button
              block
              size="small"
              type={
                isDatesSegmentMode && fastFilter === LAST_N_DAYS
                  ? 'primary'
                  : 'text'
              }
            >
              Последних дней:
            </Button>
            <InputNumber
              ref={inputRef}
              controls={false}
              size="small"
              type="number"
              value={lastNDays}
              onChange={(value) => {
                const userLastDay = new Date(today);

                userLastDay.setDate(userLastDay.getDate() - Number(value));
                updateDateFilters(
                  moment(userLastDay).format(dateFormatList[1]),
                  moment(today).format(dateFormatList[1])
                );

                setLastNDays(value);
              }}
              onKeyDown={(event) => {
                if (event.key === '-' || event.key === '+') {
                  event.preventDefault();
                }
              }}
            />
          </Space>

          <Divider className="applying-button" />
          <Row justify="end">
            <Button
              type="primary"
              size="small"
              disabled={handleDisabling(
                CHF_VISITS_FILTER,
                CHF_PERIOD,
                false,
                '',
                filterGroupIndex
              )}
              onClick={() => {
                setUpServerDataChunk(
                  CHF_VISITS_FILTER,
                  CHF_PERIOD,
                  false,
                  '',
                  filterGroupIndex,
                  true
                );

                hideFilterBlock(
                  '.visits-period-label',
                  innerWidth,
                  filterGroupIndex
                );
              }}
            >
              Выбрать
            </Button>
          </Row>
        </Space>
      </div>
    </div>
  );
};
